import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { documentosAPI, fabricantesAPI, talleresAPI, comprasAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import PDFViewerPro from '../../components/PDFViewerPro';
import { 
  Search, FileText, Eye, EyeOff, ShoppingCart, 
  Lock, Unlock, BookOpen, Loader2, Filter, X, Newspaper, CheckSquare, Star, TrendingUp, History, Globe, ExternalLink
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import useFavoritos from '../../hooks/useFavoritos';
import { formatApiError } from '../../utils/apiError';
import { misDocumentosAPI } from '../../services/api';

// ID estable para la tarjeta hardcodeada de Continental Aerospace Technologies.
// Permite participar en favoritos / "Tus accesos rápidos" (registrarApertura) usando la
// misma colección que cualquier otro manual real.
const CONTINENTAL_IPC_ID = 'continental_ipc_manual';
const CONTINENTAL_IPC_URL = 'https://continental.aero/ipc/?aribrand=CAT';

// Tarjetas hardcodeadas de Autoridades Aeronáuticas (ANAC / FAA).
// Son enlaces a las webs oficiales de Directivas de Aeronavegabilidad (no PDFs).
// Se muestran PRIMERAS en "Publicaciones de Servicio" porque siempre tienen la fecha de hoy.
// `matchNombre` se compara contra el nombre del fabricante/autoridad normalizado (solo letras).
const AUTORIDAD_AD_CARDS = [
  {
    id: 'faa_ad_link',
    matchNombre: 'faa',
    titulo: 'ADs FAA',
    ref: 'drs.faa.gov',
    autoridad: 'Federal Aviation Administration',
    url: 'https://drs.faa.gov/',
  },
  {
    id: 'anac_ad_link',
    matchNombre: 'anac',
    titulo: 'Directivas y Advertencias de Aeronavegabilidad',
    ref: 'argentina.gob.ar/anac',
    autoridad: 'Administración Nacional de Aviación Civil',
    url: 'https://www.argentina.gob.ar/anac/aeronaves/directivas-y-advertencias-de-aeronavegabilidad',
  },
];
const AUTORIDAD_AD_IDS = AUTORIDAD_AD_CARDS.map(c => c.id);
const normalizarNombreFab = (nombre) => (nombre || '').toLowerCase().replace(/[^a-z]/g, '');

// Helper para formatear fechas sin desfase de timezone
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  // Parsear solo la parte de fecha (YYYY-MM-DD) para evitar problemas de timezone
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return dateStr;
  // Crear fecha usando componentes locales (no UTC)
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('es-AR');
};

// Frases aeronáuticas para el overlay de carga (cicladas mientras se abre el visor)
const AVIATION_QUOTES = [
  "Preparando cabina… el conocimiento despega en instantes.",
  "\"La aviación es la prueba de que, dado el deseo, tenemos la capacidad de lograr lo imposible.\" — Eddie Rickenbacker",
  "Alineando con la pista… tu manual está por aterrizar.",
  "\"Volar es aprender a lanzarte al suelo y errar.\" — Douglas Adams",
  "Verificando checklist… cargando páginas técnicas.",
  "\"Una vez que hayas probado el vuelo, siempre caminarás con la vista hacia el cielo.\" — Leonardo da Vinci",
  "Estableciendo comunicación con torre de control…",
  "\"El piloto que no tiene miedo está muerto.\" — Louis Blériot",
  "Ascendiendo a nivel de crucero… documento en camino.",
];

const TallerDocumentos = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [manuales, setManuales] = useState([]);
  const [fabricantes, setFabricantes] = useState([]);
  const [taller, setTaller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  // Overlay inmediato cuando se abre desde link de notificación (?abrir=<id>).
  // Se muestra en cuanto se monta la página, sin esperar a que carguen los 400+ manuales.
  const abrirIdRef = useRef(searchParams.get('abrir'));
  const [notifOpening, setNotifOpening] = useState(() => Boolean(searchParams.get('abrir')));
  const [notifQuoteIdx, setNotifQuoteIdx] = useState(0);
  const notifHandledRef = useRef(false);
  
  // Tab activa (leída de URL o default)
  const tabFromUrl = searchParams.get('tab');
  const tipoFromUrl = searchParams.get('tipo');  // priorización: ?tipo=service_bulletin o ?tipo=directiva_ad
  const defaultTab = tabFromUrl && ['manuales', 'publicaciones_servicio', 'otras'].includes(tabFromUrl) 
    ? tabFromUrl 
    : 'manuales';
  const [activeTab, setActiveTab] = useState(defaultTab);
  
  // Filtros
  const [filterFabricante, setFilterFabricante] = useState('');
  const [filterModelo, setFilterModelo] = useState('');
  const [filterModelos, setFilterModelos] = useState([]);
  const [soloMisCapacidades, setSoloMisCapacidades] = useState(false);
  // Filtro contextual por tipo de documento (depende de la solapa activa).
  // Se inicializa con ?tipo=... si vino por acceso rápido desde Dashboard/Home.
  const [filterTipo, setFilterTipo] = useState(tipoFromUrl || '');

  // Visibilidad de "Más usados / Último abierto":
  // - Si vino por acceso rápido específico (?tipo=...) → arranca OCULTO.
  // - Si entró directo a /panel/documentos → arranca VISIBLE.
  const [topVisible, setTopVisible] = useState(() => !tipoFromUrl);
  const autoSwitchedRef = useRef(false);  // evita re-disparar toast de auto-switch en re-renders

  // Hook aislado: favoritos + más usados + progreso (incluye fecha_apertura por doc)
  const { favoritos, masUsados, progresoPorManual, toggleFavorito, reload: reloadFavoritos, loading: favsLoading } = useFavoritos();

  // Snapshot de favoritos congelado al primer load. Se usa SOLO para ordenar la lista
  // (favoritos arriba). Evita que las tarjetas salten de posición al togglear ⭐.
  // La estrella se sigue actualizando al instante via el Set `favoritos` (live).
  const [favoritosForSort, setFavoritosForSort] = useState(new Set());
  const favsSortLoadedRef = useRef(false);
  useEffect(() => {
    if (!favsLoading && !favsSortLoadedRef.current) {
      setFavoritosForSort(new Set(favoritos));
      favsSortLoadedRef.current = true;
    }
  }, [favsLoading, favoritos]);

  // TIPOS DE DOCUMENTOS - Lista completa
  const tiposDocumento = [
    // Manuales
    { value: 'manual_mantenimiento', label: 'Manual de Mantenimiento', categoria: 'Manuales' },
    { value: 'manual_servicio', label: 'Manual de Servicio', categoria: 'Manuales' },
    { value: 'catalogo_partes', label: 'Catálogo Ilustrado de Partes', categoria: 'Manuales' },
    { value: 'manual_practicas_estandar', label: 'Manual de Prácticas Estándar', categoria: 'Manuales' },
    { value: 'overhaul_manual', label: 'Overhaul Manual', categoria: 'Manuales' },
    { value: 'publicacion_tecnica', label: 'Publicación Técnica', categoria: 'Manuales' },
    { value: 'inspection_report', label: 'Inspection Report', categoria: 'Manuales' },
    { value: 'progressive_inspection', label: 'Progressive Inspection', categoria: 'Manuales' },
    { value: 'wiring_diagram', label: 'Wiring Diagram', categoria: 'Manuales' },
    { value: 'programmed_inspection', label: 'Programmed Inspection', categoria: 'Manuales' },
    { value: 'airworthiness_program', label: 'Airworthiness Program', categoria: 'Manuales' },
    { value: 'application_guide', label: 'Application Guide', categoria: 'Manuales' },
    { value: 'tools_catalog', label: 'Tools Catalog', categoria: 'Manuales' },
    { value: 'manual_operaciones', label: 'Manual de Operaciones', categoria: 'Manuales' },
    { value: 'manual_propietario', label: 'Manual de Propietario', categoria: 'Manuales' },
    { value: 'manual_reparacion', label: 'Manual de Reparación Estructural', categoria: 'Manuales' },
    { value: 'shop_manual', label: 'Shop Manual', categoria: 'Manuales' },
    { value: 'special_inspection', label: 'Special Inspection', categoria: 'Manuales' },
    { value: 'trouble_shooting', label: 'Trouble Shooting', categoria: 'Manuales' },
    // Publicaciones de Servicio
    { value: 'directiva_ad', label: 'Directiva de Aeronavegabilidad', categoria: 'Publicaciones de Servicio' },
    { value: 'service_bulletin', label: 'Service Bulletin', categoria: 'Publicaciones de Servicio' },
    // Otras Publicaciones
    { value: 'service_letter', label: 'Service Letter', categoria: 'Otras Publicaciones' },
    { value: 'service_instruction', label: 'Service Instruction', categoria: 'Otras Publicaciones' },
    { value: 'service_advisory', label: 'Service Advisory', categoria: 'Otras Publicaciones' },
    { value: 'circular_tecnica', label: 'Circular Técnica', categoria: 'Otras Publicaciones' },
  ];

  const tiposManuales = tiposDocumento.filter(t => t.categoria === 'Manuales').map(t => t.value);
  const tiposPublicacionesServicio = tiposDocumento.filter(t => t.categoria === 'Publicaciones de Servicio').map(t => t.value);
  const tiposOtrasPublicaciones = tiposDocumento.filter(t => t.categoria === 'Otras Publicaciones').map(t => t.value);

  useEffect(() => {
    loadData();
  }, []);

  // FAST-PATH: si vinimos desde una notificación (?abrir=<id>), fetcheamos SOLO el
  // documento pedido + info del taller en paralelo, y abrimos el visor sin esperar
  // los 400+ manuales. loadData() sigue corriendo en background para hidratar la lista.
  useEffect(() => {
    const abrirId = abrirIdRef.current;
    if (!abrirId || notifHandledRef.current) return;
    notifHandledRef.current = true;
    (async () => {
      try {
        const [docRes, tallerRes] = await Promise.all([
          documentosAPI.getManual(abrirId),
          user?.taller_id ? talleresAPI.getOne(user.taller_id) : Promise.resolve({ data: null }),
        ]);
        const doc = docRes?.data?.data || docRes?.data;
        if (!doc || !doc.id) {
          toast.error('No se encontró el documento solicitado');
          setNotifOpening(false);
          return;
        }
        if (tallerRes?.data) setTaller(tallerRes.data);
        setSelectedDoc(doc);
        setShowPdfViewer(true);
      } catch (err) {
        toast.error('Error al abrir el documento');
        setNotifOpening(false);
      }
    })();
  }, [user?.taller_id]);

  // Cerrar overlay una vez que el visor se abrió (el visor toma control con su propio loader).
  useEffect(() => {
    if (notifOpening && showPdfViewer) {
      setNotifOpening(false);
    }
  }, [notifOpening, showPdfViewer]);

  // Ciclado de frases aeronáuticas mientras el overlay está visible.
  useEffect(() => {
    if (!notifOpening) return;
    const t = setInterval(() => {
      setNotifQuoteIdx((i) => (i + 1) % AVIATION_QUOTES.length);
    }, 3500);
    return () => clearInterval(t);
  }, [notifOpening]);

  // Si vienen con ?abrir={manual_id} (link de notificación), auto-abrir el visor.
  // Fallback: si el fast-path falló y el doc ya cargó en la lista, abrimos igual.
  // Nota: el parámetro &pagina=N llega pero NO se usa (ver nota histórica en PDFViewerPro
  // sobre el conflicto con useReadingProgress). Igual lo dejamos en el link por compatibilidad.
  useEffect(() => {
    const abrirId = searchParams.get('abrir');
    if (!abrirId || manuales.length === 0) return;
    if (showPdfViewer || selectedDoc?.id === abrirId) return;
    const doc = manuales.find(m => m.id === abrirId);
    if (!doc) return;
    setSelectedDoc(doc);
    setShowPdfViewer(true);
  }, [manuales, searchParams, showPdfViewer, selectedDoc]);

  useEffect(() => {
    if (filterFabricante) {
      loadFilterModelos(filterFabricante);
    } else {
      setFilterModelos([]);
      setFilterModelo('');
    }
  }, [filterFabricante]);

  const loadData = async () => {
    try {
      // Cargar primer lote rápido (50 docs) + fabricantes + taller en paralelo
      const [manualesRes, fabRes, tallerRes] = await Promise.all([
        documentosAPI.getManuales({ limit: 50, page: 1 }),
        fabricantesAPI.getAll({ limit: 500 }),
        user?.taller_id ? talleresAPI.getOne(user.taller_id) : Promise.resolve({ data: null })
      ]);
      const manualesData = manualesRes.data?.data || manualesRes.data || [];
      const fabricantesData = fabRes.data?.data || fabRes.data || [];
      setManuales(Array.isArray(manualesData) ? manualesData : []);
      setFabricantes(Array.isArray(fabricantesData) ? fabricantesData : []);
      setTaller(tallerRes.data);
      setLoading(false);

      // Cargar el resto en background por páginas
      const totalPages = manualesRes.data?.pagination?.pages || 1;
      if (totalPages > 1) {
        const allManuales = [...(Array.isArray(manualesData) ? manualesData : [])];
        for (let page = 2; page <= totalPages; page++) {
          try {
            const nextRes = await documentosAPI.getManuales({ limit: 50, page });
            const nextData = nextRes.data?.data || [];
            if (Array.isArray(nextData) && nextData.length > 0) {
              allManuales.push(...nextData);
              setManuales([...allManuales]);
            }
          } catch {
            break;
          }
        }
      }
    } catch (error) {
      toast.error('Error al cargar documentos');
      setLoading(false);
    }
  };

  const loadFilterModelos = async (fabId) => {
    try {
      const response = await fabricantesAPI.getModelos(fabId);
      setFilterModelos(response.data);
    } catch (error) {
      setFilterModelos([]);
    }
  };

  // Obtener permisos de impresión según nivel de suscripción
  const getPermisosImpresion = () => {
    if (!taller) return { puede_imprimir: false, max_paginas: 0 };
    
    const nivelSuscripcion = taller.nivel_suscripcion || 'status_report';
    const permisos = {
      'status_report': { puede_imprimir: false, max_paginas: 0 },
      'estandar': { puede_imprimir: true, max_paginas: 25 },
      'estandar_pro': { puede_imprimir: true, max_paginas: 25 },
      'premium': { puede_imprimir: true, max_paginas: 25 },
      'sin_suscripcion': { puede_imprimir: false, max_paginas: 0 }
    };
    
    return permisos[nivelSuscripcion] || permisos['status_report'];
  };

  const canView = (doc) => {
    if (!taller) return false;
    
    // IMPORTANTE: Si el usuario cedió su acceso a documentos, no puede ver nada
    if (user?.tiene_acceso_docs === false) {
      return false;
    }
    
    // Obtener permisos basados en nivel de suscripción
    const nivelSuscripcion = taller.nivel_suscripcion || 'status_report';
    const permisosNivel = {
      'status_report': { puede_ver_contenido: false, puede_imprimir: false, max_paginas: 0 },
      'estandar': { puede_ver_contenido: true, puede_imprimir: true, max_paginas: 25 },
      'estandar_pro': { puede_ver_contenido: true, puede_imprimir: true, max_paginas: 25 },
      'premium': { puede_ver_contenido: true, puede_imprimir: true, max_paginas: 25 },
      'sin_suscripcion': { puede_ver_contenido: false, puede_imprimir: false, max_paginas: 0 }
    };
    const permisos = permisosNivel[nivelSuscripcion] || permisosNivel['status_report'];
    
    // Si compró el documento específico
    if (taller.documentos_comprados?.includes(doc.id)) return true;
    
    // Si compró algún modelo del documento
    const modelosDoc = doc.modelos_aplicables || [];
    if (modelosDoc.some(m => taller.modelos_comprados?.includes(m))) return true;
    
    // Si tiene capacidad temporal activa para algún modelo del documento
    const capacidadesTemporales = taller.capacidades_temporales || [];
    const ahora = new Date();
    const capacidadesTempActivas = capacidadesTemporales
      .filter(ct => new Date(ct.fecha_fin) > ahora)
      .map(ct => ct.modelo_id);
    if (modelosDoc.some(m => capacidadesTempActivas.includes(m))) return true;
    
    // Estándar y Premium pueden ver documentos de sus capacidades ANAC
    if (permisos.puede_ver_contenido) {
      const capacidadesTaller = taller.capacidades_anac || [];
      const modelosComprados = taller.modelos_comprados || [];
      
      // AGREGAR: Capacidades temporales activas (no expiradas)
      const capacidadesTemporales = (taller.capacidades_temporales || [])
        .filter(ct => new Date(ct.fecha_expiracion) > new Date())
        .flatMap(ct => ct.modelos_ids || []);
      
      // Combinar todas las capacidades
      const todasCapacidades = [...capacidadesTaller, ...modelosComprados, ...capacidadesTemporales];
      
      // Extraer nombres de modelos de las capacidades (formato: fabricante_id_nombre_modelo)
      const nombresModelosCapacidades = todasCapacidades.map(cap => {
        // El formato es: fabricante_id_nombre_modelo (ej: 551a4475-39dc-4d39-ab72-e6613f98baf1_PA-28-181 ARCHER)
        const partes = cap.split('_');
        if (partes.length >= 2) {
          // Todo después del primer underscore es el nombre del modelo
          return partes.slice(1).join('_');
        }
        return cap;
      });
      
      // Verificar si algún modelo del documento coincide con las capacidades del taller
      // También verificar el fabricante para mayor precisión
      const fabricanteDoc = doc.fabricante_id;
      
      for (const modeloDoc of modelosDoc) {
        // Verificar coincidencia directa con nombre de modelo
        if (nombresModelosCapacidades.includes(modeloDoc)) {
          return true;
        }
        
        // También verificar si la capacidad completa (fabricante_modelo) coincide
        // Esto es para casos donde modelos_aplicables contiene el ID completo
        if (todasCapacidades.includes(modeloDoc)) {
          return true;
        }
        
        // Verificar coincidencia con el ID completo (fabricante_modelo)
        if (fabricanteDoc) {
          const capacidadCompleta = `${fabricanteDoc}_${modeloDoc}`;
          if (todasCapacidades.includes(capacidadCompleta)) {
            return true;
          }
        }
      }
    }
    
    return false;
  };

  const handleAddToCart = async (doc) => {
    setAddingToCart(true);
    try {
      await comprasAPI.agregarAlCarrito({
        tipo: 'documento',
        documento_id: doc.id,
        nombre: doc.nombre || doc.titulo,
        precio_usd: doc.precio_usd || 0
      });
      toast.success('Agregado al carrito');
    } catch (error) {
      toast.error(formatApiError(error, 'Error al agregar'));
    } finally {
      setAddingToCart(false);
    }
  };

  const handleRequestManual = async (doc) => {
    try {
      await comprasAPI.solicitarManual({
        taller_id: user.taller_id,
        documento_id: doc.id,
        documento_tipo: 'manual',
        documento_nombre: doc.nombre,
        precio_usd: doc.precio_usd || 0
      });
      toast.success('Documento agregado al carrito. Ve al carrito para completar la compra.');
    } catch (error) {
      toast.error(formatApiError(error, 'Error al solicitar'));
    }
  };

  const getFabricanteName = (fabId) => {
    return fabricantes.find(f => f.id === fabId)?.nombre || 'Desconocido';
  };

  const getTipoLabel = (tipoValue) => {
    const tipo = tiposDocumento.find(t => t.value === tipoValue);
    return tipo ? tipo.label : tipoValue;
  };

  const clearFilters = () => {
    setFilterFabricante('');
    setFilterModelo('');
    setFilterTipo('');
    setSearchTerm('');
    setSoloMisCapacidades(false);
  };

  // Filtrar documentos
  const filterDocumentos = (docs, opts = {}) => {
    const skipTipo = opts.skipTipo === true;
    return docs.filter(doc => {
      const matchSearch = !searchTerm || 
        doc.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.numero_referencia?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtrado de fabricante - mostrar docs que:
      // 1. Fueron CREADOS por el fabricante (fabricante_id)
      // 2. O que AFECTAN a modelos de ese fabricante
      let matchFabricante = true;
      if (filterFabricante) {
        const esCreador = doc.fabricante_id === filterFabricante;
        const afectaModelosDelFabricante = (doc.modelos_aplicables || []).some(modeloRef => {
          if (modeloRef.startsWith(filterFabricante + '_')) return true;
          return false;
        });
        matchFabricante = esCreador || afectaModelosDelFabricante;
      }
      
      // Filtrado de modelo - manejar ambos formatos
      let matchModelo = true;
      if (filterModelo && doc.modelos_aplicables) {
        matchModelo = doc.modelos_aplicables.some(modeloRef => {
          if (modeloRef === filterModelo) return true;
          if (filterFabricante && modeloRef.startsWith(filterFabricante + '_')) {
            const modeloNombre = modeloRef.substring(filterFabricante.length + 1);
            return modeloNombre === filterModelo;
          }
          const parts = modeloRef.split('_');
          if (parts.length > 1) {
            const modeloNombre = parts.slice(1).join('_');
            return modeloNombre === filterModelo;
          }
          return false;
        });
      }
      
      // Filtro "Solo mis capacidades"
      let matchCapacidades = true;
      if (soloMisCapacidades && taller) {
        const capacidadesTaller = taller.capacidades_anac || [];
        const modelosComprados = taller.modelos_comprados || [];
        const capacidadesTemporales = (taller.capacidades_temporales || [])
          .filter(ct => new Date(ct.fecha_expiracion) > new Date())
          .flatMap(ct => ct.modelos_ids || []);
        const todasCapacidades = [...capacidadesTaller, ...modelosComprados, ...capacidadesTemporales];
        const modelosDoc = doc.modelos_aplicables || [];
        // Manejar formato nuevo en capacidades
        matchCapacidades = modelosDoc.some(modeloRef => {
          let modeloNombre = modeloRef;
          const parts = modeloRef.split('_');
          if (parts.length > 1 && parts[0].includes('-')) {
            modeloNombre = parts.slice(1).join('_');
          }
          return todasCapacidades.includes(modeloRef) || todasCapacidades.includes(modeloNombre);
        });
      }
      
      // Filtro por tipo de documento (contextual a la solapa activa)
      const matchTipo = skipTipo || !filterTipo || doc.tipo_manual === filterTipo;

      return matchSearch && matchFabricante && matchModelo && matchCapacidades && matchTipo;
    });
  };

  // Ordenar documentos:
  //   0. Si vino por ?tipo=... → priorizar docs de ese tipo arriba (mantiene el resto del orden interno).
  //   1. FAVORITOS primero (absoluto, sin importar capacidades).
  //   2. Luego los de mis capacidades por fecha de revisión (más recientes primero).
  //   3. Luego el resto por fecha de revisión.
  const sortDocumentos = (docs) => {
    if (!taller) return docs;
    
    const capacidadesTaller = taller.capacidades_anac || [];
    const modelosComprados = taller.modelos_comprados || [];
    const todasCapacidades = [...capacidadesTaller, ...modelosComprados];
    
    const sortByFecha = (a, b) => {
      const fechaA = a.fecha_revision ? new Date(a.fecha_revision) : new Date(0);
      const fechaB = b.fecha_revision ? new Date(b.fecha_revision) : new Date(0);
      return fechaB - fechaA;
    };

    // 1. Separar favoritos vs no-favoritos (usando snapshot estable para no mover tarjetas al togglear)
    const favs = [];
    const noFavs = [];
    docs.forEach(doc => {
      if (favoritosForSort.has(doc.id)) favs.push(doc);
      else noFavs.push(doc);
    });
    
    // 2. Dentro de no-favoritos, separar por capacidades
    const dentroCapacidades = [];
    const fueraCapacidades = [];
    noFavs.forEach(doc => {
      const modelosDoc = doc.modelos_aplicables || [];
      const estaEnCapacidades = modelosDoc.some(m => todasCapacidades.includes(m));
      if (estaEnCapacidades) dentroCapacidades.push(doc);
      else fueraCapacidades.push(doc);
    });

    // Ordenar cada grupo por fecha
    favs.sort(sortByFecha);
    dentroCapacidades.sort(sortByFecha);
    fueraCapacidades.sort(sortByFecha);

    // Resultado base: favoritos absolutos → capacidades → resto
    const baseOrden = [...favs, ...dentroCapacidades, ...fueraCapacidades];

    // 0. Priorización por ?tipo=... (acceso rápido desde Dashboard/Home).
    //    Re-particiona el resultado: docs del tipo elegido primero, conservando el resto del orden.
    if (tipoFromUrl) {
      const delTipo = baseOrden.filter(d => d.tipo_manual === tipoFromUrl);
      const restoTipo = baseOrden.filter(d => d.tipo_manual !== tipoFromUrl);
      return [...delTipo, ...restoTipo];
    }

    return baseOrden;
  };

  // Filtrar por categoría y ordenar
  const getManuales = () => sortDocumentos(filterDocumentos(manuales.filter(m => tiposManuales.includes(m.tipo_manual))));
  const getPublicacionesServicio = () => sortDocumentos(filterDocumentos(manuales.filter(m => tiposPublicacionesServicio.includes(m.tipo_manual))));
  const getOtrasPublicaciones = () => sortDocumentos(filterDocumentos(manuales.filter(m => 
    tiposOtrasPublicaciones.includes(m.tipo_manual) || 
    (!tiposManuales.includes(m.tipo_manual) && !tiposPublicacionesServicio.includes(m.tipo_manual))
  )));

  // Último documento abierto por el usuario (basado en fecha_apertura más reciente).
  // Solo retorna el doc si existe en la lista cargada Y el usuario puede verlo.
  const getUltimoAbierto = () => {
    if (!progresoPorManual || Object.keys(progresoPorManual).length === 0) return null;
    let ultimoId = null;
    let ultimaFecha = 0;
    for (const [manualId, info] of Object.entries(progresoPorManual)) {
      const fechaStr = info?.fecha_apertura;
      if (!fechaStr) continue;
      const ts = new Date(fechaStr).getTime();
      if (!isNaN(ts) && ts > ultimaFecha) {
        ultimaFecha = ts;
        ultimoId = manualId;
      }
    }
    if (!ultimoId) return null;
    // Caso especial: tarjeta virtual Continental
    if (ultimoId === CONTINENTAL_IPC_ID) {
      return mostrarTarjetaContinental ? continentalSyntheticDoc : null;
    }
    // Caso especial: tarjetas virtuales de Autoridades (ANAC / FAA)
    if (AUTORIDAD_AD_IDS.includes(ultimoId)) {
      return puedeVerTarjetasAutoridad ? getAutoridadSyntheticDoc(ultimoId) : null;
    }
    const doc = manuales.find(m => m.id === ultimoId);
    return doc || null;
  };

  // Top 5 documentos más usados por el usuario (en el orden devuelto por el backend).
  // Sólo incluye los que existen en la lista cargada. Excluye el "último abierto" si ya aparece
  // para no duplicar (mostramos la card una sola vez, priorizando el badge "Último abierto").
  const getMasUsados = () => {
    if (!masUsados || masUsados.length === 0) return [];
    const ultimo = getUltimoAbierto();
    const excluirId = ultimo?.id;
    const byId = new Map(manuales.map(m => [m.id, m]));
    const result = [];
    for (const id of masUsados) {
      if (id === excluirId) continue;
      if (id === CONTINENTAL_IPC_ID) {
        if (mostrarTarjetaContinental) result.push(continentalSyntheticDoc);
        continue;
      }
      if (AUTORIDAD_AD_IDS.includes(id)) {
        if (puedeVerTarjetasAutoridad) result.push(getAutoridadSyntheticDoc(id));
        continue;
      }
      const doc = byId.get(id);
      if (doc) result.push(doc);
    }
    return result.slice(0, 5);
  };

  // Componente de tarjeta de documento
  const DocumentCard = ({ doc }) => {
    const puedeVer = canView(doc);
    const esFavorito = favoritos.has(doc.id);
    
    return (
      <Card className="hover:shadow-lg transition-shadow relative h-full flex flex-col">
        {/* Botón estrella favorito (esquina superior derecha) */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleFavorito(doc.id); }}
          className="absolute top-2 right-2 z-10 p-1.5 rounded-full hover:bg-amber-50 transition-colors"
          aria-label={esFavorito ? 'Quitar de favoritos' : 'Marcar como favorito'}
          title={esFavorito ? 'Quitar de favoritos' : 'Marcar como favorito'}
          data-testid={`favorito-btn-${doc.id}`}
        >
          <Star
            className={`h-5 w-5 ${
              esFavorito
                ? 'fill-amber-400 text-amber-500'
                : 'text-gray-300 hover:text-amber-400'
            }`}
          />
        </button>
        <CardHeader className="pb-2 pr-10">
          <div className="flex items-start justify-between">
            <CardTitle className="text-base line-clamp-2 min-h-[3rem]">{doc.nombre}</CardTitle>
            {puedeVer ? (
              <Unlock className="h-4 w-4 text-green-500 flex-shrink-0 ml-1" />
            ) : (
              <Lock className="h-4 w-4 text-gray-400 flex-shrink-0 ml-1" />
            )}
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <div className="space-y-2 mb-4">
            <p className="text-sm text-gray-500">Ref: {doc.numero_referencia}</p>
            <p className="text-sm text-gray-500">
              Rev: {doc.revision} 
              {doc.fecha_revision && (
                <span className="ml-2">({formatDate(doc.fecha_revision)})</span>
              )}
            </p>
            <p className="text-xs text-gray-400">{getFabricanteName(doc.fabricante_id)}</p>
            <div className="flex items-center justify-between">
              <Badge variant="outline">{getTipoLabel(doc.tipo_manual)}</Badge>
              {/* Precio oculto - se muestra solo en el carrito después de aprobación */}
            </div>
          </div>
          
          <div className="flex gap-2 mt-auto">
            {puedeVer ? (
              <>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1 border-2 border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white"
                  onClick={() => { setSelectedDoc(doc); setShowPdfViewer(true); }}
                  disabled={!doc.pdf_url && !doc.gridfs_id && !doc.pdf_file_id}
                >
                  <Eye className="h-4 w-4 mr-1" /> Ver
                </Button>
              </>
            ) : (
              <Button 
                size="sm" 
                className="w-full"
                onClick={() => taller?.nivel_suscripcion === 'status_report' 
                  ? handleRequestManual(doc) 
                  : handleAddToCart(doc)
                }
                disabled={addingToCart}
              >
                {taller?.nivel_suscripcion === 'status_report' ? (
                  <>Solicitar Documento</>  
                ) : (
                  <><ShoppingCart className="h-4 w-4 mr-1" /> Agregar al carrito</>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // ============================================================
  // TARJETA ESPECIAL: Continental publica su Illustrated Parts
  // Catalog EN LÍNEA en su web oficial (no como PDF descargable).
  // Se muestra PRIMERA en "Manuales Técnicos" cuando el usuario
  // filtra por Continental o por tipo "Catálogo Ilustrado de Partes".
  // Soporta favoritos y "Tus accesos rápidos" usando el ID estable
  // CONTINENTAL_IPC_ID.
  // ============================================================
  const fabricanteContinental = useMemo(
    () => fabricantes.find(f => (f.nombre || '').toLowerCase().includes('continental')),
    [fabricantes]
  );

  const mostrarTarjetaContinental = useMemo(() => {
    if (!fabricanteContinental || !taller) return false;
    if (user?.tiene_acceso_docs === false) return false;
    // Niveles que pueden ver contenido (status_report / sin_suscripcion quedan fuera)
    const nivel = taller.nivel_suscripcion || 'status_report';
    if (!['estandar', 'estandar_pro', 'premium'].includes(nivel)) return false;
    // Acceso a ≥1 modelo de Continental: capacidades ANAC, modelos comprados
    // o capacidades temporales activas (formato: fabricante_id_nombre_modelo)
    const ahora = new Date();
    const capacidadesTemp = (taller.capacidades_temporales || [])
      .filter(ct => new Date(ct.fecha_expiracion || ct.fecha_fin) > ahora)
      .flatMap(ct => ct.modelos_ids || (ct.modelo_id ? [ct.modelo_id] : []));
    const todasCapacidades = [
      ...(taller.capacidades_anac || []),
      ...(taller.modelos_comprados || []),
      ...capacidadesTemp,
    ];
    const tieneCapacidades = todasCapacidades.some(
      cap => typeof cap === 'string' && cap.startsWith(fabricanteContinental.id + '_')
    );
    if (!tieneCapacidades) return false;

    // Visibilidad según filtros activos:
    //  a) Filtro fabricante = Continental → mostrar
    //  b) Filtro tipo de documento = "Catálogo Ilustrado de Partes" sin filtro de fabricante distinto → mostrar
    //  c) Sin filtros relevantes → mostrar (la tarjeta es destacada para Continental igual)
    const fabFiltradoEsContinental = filterFabricante === fabricanteContinental.id;
    const tipoFiltradoEsCIP = filterTipo === 'catalogo_partes';
    const sinFiltroDeFabricante = !filterFabricante;

    if (fabFiltradoEsContinental) return true;
    if (tipoFiltradoEsCIP && sinFiltroDeFabricante) return true;
    return false;
  }, [fabricanteContinental, taller, filterFabricante, filterTipo, user]);

  // Doc sintético para que la tarjeta Continental pueda viajar por las mismas
  // estructuras (ultimoAbierto / topDocs / renderCard) que un manual real.
  const continentalSyntheticDoc = useMemo(() => ({
    id: CONTINENTAL_IPC_ID,
    isContinentalIPC: true,
    nombre: 'Illustrated Parts Catalog — Continental Aerospace Technologies',
    fabricante_id: fabricanteContinental?.id,
    tipo_manual: 'catalogo_partes',
  }), [fabricanteContinental]);

  // Click sobre "Ver en sitio oficial" → abrir URL externa + registrar apertura
  // para que la tarjeta aparezca en "Tus accesos rápidos" (último abierto + más usados).
  const handleContinentalOpen = () => {
    // Abrir PRIMERO sincrónicamente para evitar bloqueo de popups
    window.open(CONTINENTAL_IPC_URL, '_blank', 'noopener,noreferrer');
    // Registrar apertura en background y refrescar resumen para reflejar en "Accesos rápidos"
    misDocumentosAPI.registrarApertura(CONTINENTAL_IPC_ID)
      .then(() => reloadFavoritos())
      .catch(() => {});
  };

  const ContinentalIPCCard = ({ hideOnlineBadge = false }) => {
    const esFavorito = favoritos.has(CONTINENTAL_IPC_ID);
    const hoyFormatted = new Date().toLocaleDateString('es-AR');
    return (
      <Card className="hover:shadow-lg transition-shadow relative border-sky-300 h-full flex flex-col" data-testid="continental-ipc-card">
        {!hideOnlineBadge && (
          <div className="absolute -top-2 left-3 z-20 flex items-center gap-1 bg-sky-600 text-white text-xs font-medium px-2 py-0.5 rounded-full shadow-sm">
            <Globe className="h-3 w-3" />
            Catálogo en línea — Web oficial
          </div>
        )}
        {/* Botón estrella favorito (esquina superior derecha) */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleFavorito(CONTINENTAL_IPC_ID); }}
          className="absolute top-2 right-2 z-10 p-1.5 rounded-full hover:bg-amber-50 transition-colors"
          aria-label={esFavorito ? 'Quitar de favoritos' : 'Marcar como favorito'}
          title={esFavorito ? 'Quitar de favoritos' : 'Marcar como favorito'}
          data-testid={`favorito-btn-${CONTINENTAL_IPC_ID}`}
        >
          <Star
            className={`h-5 w-5 ${
              esFavorito ? 'fill-amber-400 text-amber-500' : 'text-gray-300 hover:text-amber-400'
            }`}
          />
        </button>
        <CardHeader className="pb-2 pt-5 pr-10">
          <div className="flex items-start justify-between">
            <CardTitle className="text-base line-clamp-2 min-h-[3rem]">Illustrated Parts Catalog — Continental Aerospace Technologies</CardTitle>
            <Unlock className="h-4 w-4 text-green-500 flex-shrink-0 ml-1" />
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <div className="space-y-2 mb-4">
            <p className="text-sm text-gray-500">Ref: continental.aero</p>
            <p className="text-sm text-gray-500">
              Actualizado al día de hoy <span className="ml-1 text-gray-400">({hoyFormatted})</span>
            </p>
            <p className="text-xs text-gray-400">{fabricanteContinental?.nombre || 'Continental Motors'}</p>
            <div className="flex items-center justify-between">
              <Badge variant="outline">Catálogo Ilustrado de Partes</Badge>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-auto border-2 border-sky-500 text-sky-600 hover:bg-sky-500 hover:text-white"
            onClick={handleContinentalOpen}
            data-testid="continental-ipc-ver-btn"
          >
            <ExternalLink className="h-4 w-4 mr-1" /> Ver en sitio oficial
          </Button>
        </CardContent>
      </Card>
    );
  };

  // ============================================================
  // TARJETAS ESPECIALES: Autoridades Aeronáuticas (ANAC / FAA).
  // Enlaces a las webs oficiales de Directivas de Aeronavegabilidad.
  // Visibles para todos los suscriptores con acceso (estandar/estandar_pro/premium).
  // Aparecen PRIMERAS en "Publicaciones de Servicio" (siempre con fecha de hoy).
  // ============================================================
  const puedeVerTarjetasAutoridad = useMemo(() => {
    if (!taller) return false;
    if (user?.tiene_acceso_docs === false) return false;
    const nivel = taller.nivel_suscripcion || 'status_report';
    return ['estandar', 'estandar_pro', 'premium'].includes(nivel);
  }, [taller, user]);

  // Docs sintéticos para viajar por accesos rápidos / favoritos como un manual real.
  const autoridadSyntheticDocs = useMemo(
    () => AUTORIDAD_AD_CARDS.map(c => ({
      id: c.id,
      isAutoridadAD: true,
      cardConfig: c,
      nombre: c.titulo,
      tipo_manual: 'directiva_ad',
    })),
    []
  );
  const getAutoridadSyntheticDoc = (id) => autoridadSyntheticDocs.find(d => d.id === id) || null;

  // Tarjetas visibles según filtros activos (para la solapa Publicaciones de Servicio):
  //  - Requiere acceso (nivel de suscripción).
  //  - Filtro tipo: vacío o "Directiva de Aeronavegabilidad".
  //  - Filtro fabricante/autoridad: vacío → ambas; ANAC → solo ANAC; FAA → solo FAA; otro → ninguna.
  //  - Respeta la búsqueda por texto.
  const tarjetasAutoridadVisibles = useMemo(() => {
    if (!puedeVerTarjetasAutoridad) return [];
    if (filterTipo && filterTipo !== 'directiva_ad') return [];
    const fabSeleccionado = filterFabricante
      ? fabricantes.find(f => f.id === filterFabricante)
      : null;
    const normFabSel = fabSeleccionado ? normalizarNombreFab(fabSeleccionado.nombre) : null;
    const term = (searchTerm || '').toLowerCase();
    return AUTORIDAD_AD_CARDS.filter(c => {
      if (filterFabricante) {
        // Match tolerante por substring (igual que Continental): funciona con
        // "A.N.A.C.", "ANAC", "A.N.A.C. (Argentina)", "F.A.A.", "FAA - USA", etc.
        if (!fabSeleccionado || !normFabSel) return false;
        if (!normFabSel.includes(c.matchNombre)) return false;
      }
      if (term) {
        const haystack = `${c.titulo} ${c.ref} ${c.autoridad}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [puedeVerTarjetasAutoridad, filterTipo, filterFabricante, fabricantes, searchTerm]);

  // Click "Ver en sitio oficial" → abrir URL + registrar apertura (accesos rápidos).
  const handleAutoridadOpen = (config) => {
    window.open(config.url, '_blank', 'noopener,noreferrer');
    misDocumentosAPI.registrarApertura(config.id)
      .then(() => reloadFavoritos())
      .catch(() => {});
  };

  const AutoridadADCard = ({ config, hideOnlineBadge = false }) => {
    const esFavorito = favoritos.has(config.id);
    const hoyFormatted = new Date().toLocaleDateString('es-AR');
    return (
      <Card className="hover:shadow-lg transition-shadow relative border-teal-300 h-full flex flex-col" data-testid={`autoridad-ad-card-${config.matchNombre}`}>
        {!hideOnlineBadge && (
          <div className="absolute -top-2 left-3 z-20 flex items-center gap-1 bg-teal-600 text-white text-xs font-medium px-2 py-0.5 rounded-full shadow-sm">
            <Globe className="h-3 w-3" />
            Publicaciones en línea — Web oficial
          </div>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleFavorito(config.id); }}
          className="absolute top-2 right-2 z-10 p-1.5 rounded-full hover:bg-amber-50 transition-colors"
          aria-label={esFavorito ? 'Quitar de favoritos' : 'Marcar como favorito'}
          title={esFavorito ? 'Quitar de favoritos' : 'Marcar como favorito'}
          data-testid={`favorito-btn-${config.id}`}
        >
          <Star className={`h-5 w-5 ${esFavorito ? 'fill-amber-400 text-amber-500' : 'text-gray-300 hover:text-amber-400'}`} />
        </button>
        <CardHeader className="pb-2 pt-5 pr-10">
          <div className="flex items-start justify-between">
            <CardTitle className="text-base line-clamp-2 min-h-[3rem]">{config.titulo}</CardTitle>
            <Unlock className="h-4 w-4 text-green-500 flex-shrink-0 ml-1" />
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <div className="space-y-2 mb-4">
            <p className="text-sm text-gray-500">Ref. {config.ref}</p>
            <p className="text-sm text-gray-500">
              Actualizado al día de hoy <span className="ml-1 text-gray-400">({hoyFormatted})</span>
            </p>
            <p className="text-xs text-gray-400">{config.autoridad}</p>
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="border-teal-300 text-teal-700">Directiva de Aeronavegabilidad</Badge>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-auto border-2 border-teal-500 text-teal-600 hover:bg-teal-500 hover:text-white"
            onClick={() => handleAutoridadOpen(config)}
            data-testid={`autoridad-ad-ver-btn-${config.matchNombre}`}
          >
            <ExternalLink className="h-4 w-4 mr-1" /> Ver en sitio oficial
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Renderer unificado: decide entre tarjeta normal, Continental o Autoridad AD según el doc.
  const renderCard = (doc, extraProps = {}) => {
    if (doc?.isContinentalIPC) {
      return <ContinentalIPCCard key={extraProps.key || doc.id} hideOnlineBadge={extraProps.hideOnlineBadge} />;
    }
    if (doc?.isAutoridadAD) {
      return <AutoridadADCard key={extraProps.key || doc.id} config={doc.cardConfig} hideOnlineBadge={extraProps.hideOnlineBadge} />;
    }
    return <DocumentCard key={extraProps.key || doc.id} doc={doc} />;
  };

  // Grid de documentos (extraFirst: tarjeta especial opcional que se ubica primera)
  const renderDocumentGrid = (docs, emptyMessage, extraFirst = null) => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {loading ? (
        <div className="col-span-full py-8">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-sky-600" />
            <p className="text-gray-500 text-sm">Cargando documentos de tu biblioteca...</p>
          </div>
          {/* Skeleton cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={`skel-${i}`} className="animate-pulse">
                <CardHeader className="pb-2">
                  <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-3 bg-gray-100 rounded w-2/5"></div>
                  </div>
                  <div className="h-9 bg-gray-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : (docs.length === 0 && !extraFirst) ? (
        <p className="col-span-full text-center py-8 text-gray-500">{emptyMessage}</p>
      ) : (
        <>
          {extraFirst}
          {docs.map(doc => <DocumentCard key={doc.id} doc={doc} />)}
        </>
      )}
    </div>
  );

  // Pre-calcular listas y conteos MEMOIZADOS:
  // Esto evita recomputar filtros/orden en cada render disparado por estados no relacionados
  // (toggle topVisible, auto-switch, etc.). Solo recomputa cuando cambian datos reales.
  const memoDeps = [manuales, favoritosForSort, searchTerm, filterFabricante, filterModelo, filterTipo, soloMisCapacidades, taller, tipoFromUrl];
  const listaManuales = useMemo(() => getManuales(), memoDeps);    // eslint-disable-line
  const listaPubServicio = useMemo(() => getPublicacionesServicio(), memoDeps);  // eslint-disable-line
  const listaOtras = useMemo(() => getOtrasPublicaciones(), memoDeps);  // eslint-disable-line

  const counts = {
    manuales: listaManuales.length,
    publicaciones_servicio: listaPubServicio.length + tarjetasAutoridadVisibles.length,
    otras: listaOtras.length,
  };
  const maxCount = Math.max(counts.manuales, counts.publicaciones_servicio, counts.otras);
  const ultimoAbierto = useMemo(() => getUltimoAbierto(), [progresoPorManual, manuales]);  // eslint-disable-line
  const topDocs = useMemo(() => getMasUsados(), [masUsados, manuales, progresoPorManual]);  // eslint-disable-line
  const hayFiltrosActivos = !!(searchTerm || filterFabricante || filterModelo || filterTipo || soloMisCapacidades);

  // Conteo POR TIPO para el dropdown (aplica todos los filtros salvo el de tipo y restringe a la tab activa).
  const tipoCounts = useMemo(() => {
    const tiposDeTab = activeTab === 'manuales' ? tiposManuales
                     : activeTab === 'publicaciones_servicio' ? tiposPublicacionesServicio
                     : tiposOtrasPublicaciones;
    const docsTab = filterDocumentos(manuales, { skipTipo: true }).filter(d => tiposDeTab.includes(d.tipo_manual));
    const out = { __total: docsTab.length };
    for (const d of docsTab) {
      out[d.tipo_manual] = (out[d.tipo_manual] || 0) + 1;
    }
    // Sumar tarjetas de Autoridad (ANAC/FAA) al total y al tipo directiva_ad de la solapa Pub. Servicio.
    // Se cuentan ignorando el filtro de tipo (igual que el resto del dropdown), respetando fabricante/búsqueda.
    if (activeTab === 'publicaciones_servicio' && puedeVerTarjetasAutoridad) {
      const fabSel = filterFabricante ? fabricantes.find(f => f.id === filterFabricante) : null;
      const normFabSel = fabSel ? normalizarNombreFab(fabSel.nombre) : null;
      const term = (searchTerm || '').toLowerCase();
      const cnt = AUTORIDAD_AD_CARDS.filter(c => {
        if (filterFabricante && (!normFabSel || !normFabSel.includes(c.matchNombre))) return false;
        if (term && !`${c.titulo} ${c.ref} ${c.autoridad}`.toLowerCase().includes(term)) return false;
        return true;
      }).length;
      out.__total += cnt;
      out['directiva_ad'] = (out['directiva_ad'] || 0) + cnt;
    }
    return out;
    // eslint-disable-next-line
  }, [manuales, activeTab, searchTerm, filterFabricante, filterModelo, soloMisCapacidades, puedeVerTarjetasAutoridad, fabricantes]);

  // Reglas dinámicas de visibilidad y auto-switch:
  // 3.c) Si se activa cualquier filtro y el top está visible, ocultarlo automáticamente.
  useEffect(() => {
    if (hayFiltrosActivos && topVisible) {
      setTopVisible(false);
    }
    // Solo se vigila cambios de filtros. topVisible se gestiona aparte para no forzar el cierre en re-renders.
  }, [searchTerm, filterFabricante, filterModelo, filterTipo, soloMisCapacidades]);  // eslint-disable-line

  // Al cambiar de solapa, resetear el filtro de tipo (cada solapa tiene tipos distintos).
  // Excepción: si el cambio fue programático por auto-switch del filtro (por ejemplo: el usuario buscó
  //            "Cessna" y el sistema lo mandó de Otras a Manuales), preservamos el filterTipo seleccionado.
  // Para evitar borrar accidentalmente el filtro inicial seteado por ?tipo=..., omitimos el primer render.
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    setFilterTipo('');
  }, [activeTab]);

  // Auto-switch de solapa: si la activa quedó vacía pero hay otra con resultados, ir a la de más resultados.
  // Solo se dispara cuando hay filtros activos (búsqueda/fabricante/modelo/capacidades) para no robarle
  // el control al usuario cuando explícitamente eligió una solapa vacía desde el menú.
  useEffect(() => {
    if (!hayFiltrosActivos) {
      autoSwitchedRef.current = false;
      return;
    }
    const cActiva = counts[activeTab] ?? 0;
    if (cActiva > 0) {
      autoSwitchedRef.current = false;
      return;
    }
    let bestKey = null;
    let bestCount = 0;
    for (const k of ['manuales', 'publicaciones_servicio', 'otras']) {
      if (counts[k] > bestCount) {
        bestCount = counts[k];
        bestKey = k;
      }
    }
    if (bestKey && bestCount > 0 && bestKey !== activeTab) {
      setActiveTab(bestKey);
      if (!autoSwitchedRef.current) {
        autoSwitchedRef.current = true;
        const labelTab = { manuales: 'Manuales', publicaciones_servicio: 'Pub. Servicio', otras: 'Otras Publicaciones' };
        toast.info(`Encontramos resultados en ${labelTab[bestKey]}`, { duration: 2500 });
      }
    }
  }, [counts.manuales, counts.publicaciones_servicio, counts.otras, activeTab, hayFiltrosActivos]);  // eslint-disable-line

  // Clase dinámica para cada TabsTrigger según conteo:
  // - 0 docs → estilo default gris (sin cambios).
  // - > 0 docs → mismo teal claro para todas; solo se diferencia la de mayor conteo con `font-bold`.
  const getTabClass = (tabKey) => {
    const c = counts[tabKey];
    if (c === 0) return '';
    const base = 'bg-teal-100 text-teal-800 hover:bg-teal-200 data-[state=active]:bg-teal-500 data-[state=active]:text-white data-[state=active]:shadow-md';
    if (c === maxCount) return `${base} font-bold`;
    return base;
  };

  // Click manual del usuario en una solapa → ocultar el panel "Tus accesos rápidos".
  // Se usa onClick (no onValueChange) para distinguir del auto-switch programático.
  const handleTabManualClick = () => {
    if (topVisible) setTopVisible(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4">
        <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Documentación Técnica</h1>

        {/* Mensaje si el usuario cedió su acceso */}
        {user?.tiene_acceso_docs === false && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Lock className="h-6 w-6 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Acceso a documentos deshabilitado</p>
                  <p className="text-sm text-amber-600">
                    Has cedido tu acceso a documentos a otro usuario. Para recuperarlo, ve a "Usuarios" y haz clic en "Recuperar mi acceso".
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtros - Inline para evitar re-renders que causan pérdida de foco */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Buscador */}
              <div className="relative sm:col-span-2 lg:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre o referencia..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Filtro Fabricante/Autoridad */}
              <Select value={filterFabricante} onValueChange={(v) => { setFilterFabricante(v === 'all' ? '' : v); setFilterModelo(''); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por fabricante/autoridad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los fabricantes/autoridades</SelectItem>
                  {fabricantes.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Filtro Modelo */}
              <Select 
                value={filterModelo} 
                onValueChange={(v) => setFilterModelo(v === 'all' ? '' : v)}
                disabled={!filterFabricante}
              >
                <SelectTrigger className={!filterFabricante ? 'opacity-50' : ''}>
                  <SelectValue placeholder={filterFabricante ? "Filtrar por modelo" : "Selecciona fabricante/autoridad"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los modelos</SelectItem>
                  {filterModelos.map(m => (
                    <SelectItem key={m.id || m} value={m.nombre || m}>{m.nombre || m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Filtro Tipo de Documento (contextual a la solapa activa, con conteo por opción) */}
              <Select
                value={filterTipo || 'all'}
                onValueChange={(v) => setFilterTipo(v === 'all' ? '' : v)}
              >
                <SelectTrigger data-testid="filter-tipo-documento">
                  <SelectValue placeholder="Filtrar por tipo de documento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos ({tipoCounts.__total || 0})</SelectItem>
                  {tiposDocumento
                    .filter(t => {
                      if (activeTab === 'manuales') return t.categoria === 'Manuales';
                      if (activeTab === 'publicaciones_servicio') return t.categoria === 'Publicaciones de Servicio';
                      return t.categoria === 'Otras Publicaciones';
                    })
                    .map(t => {
                      const n = tipoCounts[t.value] || 0;
                      return (
                        <SelectItem key={t.value} value={t.value} disabled={n === 0}>
                          {t.label} ({n})
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Checkbox Solo mis capacidades */}
            <div className="flex items-center gap-6 mt-4 pt-4 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="solo-capacidades" 
                  checked={soloMisCapacidades}
                  onCheckedChange={setSoloMisCapacidades}
                />
                <Label 
                  htmlFor="solo-capacidades" 
                  className="text-sm font-medium cursor-pointer flex items-center gap-2"
                >
                  <CheckSquare className="h-4 w-4 text-teal-600" />
                  Solo mostrar documentos de mis capacidades ANAC
                </Label>
              </div>
              {soloMisCapacidades && (
                <Badge className="bg-teal-100 text-teal-700">
                  Mostrando solo tus modelos habilitados
                </Badge>
              )}
            </div>
            
            {(searchTerm || filterFabricante || filterModelo || filterTipo) && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <Filter className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">Filtros activos:</span>
                {searchTerm && <Badge variant="secondary">Búsqueda: "{searchTerm}"</Badge>}
                {filterFabricante && <Badge variant="secondary">{getFabricanteName(filterFabricante)}</Badge>}
                {filterModelo && <Badge variant="secondary">{filterModelo}</Badge>}
                {filterTipo && <Badge variant="secondary">Tipo: {getTipoLabel(filterTipo)}</Badge>}
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-500 hover:text-red-700">
                  <X className="h-4 w-4 mr-1" /> Limpiar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto gap-1 bg-slate-100" data-testid="tabs-documentos">
            <TabsTrigger 
              value="manuales" 
              onClick={handleTabManualClick}
              className={`flex items-center gap-1 text-xs sm:text-sm sm:gap-2 transition-colors ${getTabClass('manuales')}`}
              data-testid="tab-manuales"
            >
              <BookOpen className="h-4 w-4 shrink-0" />
              <span className="lg:hidden">Manuales ({counts.manuales})</span>
              <span className="hidden lg:inline">Manuales Técnicos ({counts.manuales})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="publicaciones_servicio" 
              onClick={handleTabManualClick}
              className={`flex items-center gap-1 text-xs sm:text-sm sm:gap-2 transition-colors ${getTabClass('publicaciones_servicio')}`}
              data-testid="tab-publicaciones-servicio"
            >
              <Newspaper className="h-4 w-4 shrink-0" />
              <span className="truncate lg:hidden">Pub. Servicio ({counts.publicaciones_servicio})</span>
              <span className="hidden lg:inline">Publicaciones de Servicio ({counts.publicaciones_servicio})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="otras" 
              onClick={handleTabManualClick}
              className={`flex items-center gap-1 text-xs sm:text-sm sm:gap-2 transition-colors ${getTabClass('otras')}`}
              data-testid="tab-otras"
            >
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate lg:hidden">Otras ({counts.otras})</span>
              <span className="hidden lg:inline">Otras Publicaciones ({counts.otras})</span>
            </TabsTrigger>
          </TabsList>

          {/* SECCIÓN "ÚLTIMO ABIERTO + MÁS USADOS" — acceso rápido global al usuario.
              Visibilidad controlada por estado `topVisible` (reglas 3a/3b/3c). */}
          {(ultimoAbierto || topDocs.length > 0) && (
            <div className="mb-6" data-testid="mas-usados-section">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <TrendingUp className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-gray-700">Tus accesos rápidos</h2>
                {topDocs.length > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 text-xs">Top {topDocs.length}</Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTopVisible(v => !v)}
                  className="border-teal-300 text-teal-700 hover:bg-teal-50 h-7 px-2"
                  data-testid="toggle-top-docs-btn"
                >
                  {topVisible ? (
                    <><EyeOff className="h-4 w-4 mr-1" /> Ocultar</>
                  ) : (
                    <><Eye className="h-4 w-4 mr-1" /> Ver</>
                  )}
                </Button>
              </div>
              {topVisible && (
                <>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                    {/* Último abierto: primero, con badge azul + ring teal */}
                    {ultimoAbierto && (
                      <div 
                        key={`ultimo-${ultimoAbierto.id}`}
                        className="relative ring-2 ring-teal-400 ring-offset-2 rounded-lg h-full"
                        data-testid="ultimo-abierto-card"
                      >
                        {/* Badge "Último abierto" — se oculta para tarjetas de enlace (Continental / Autoridades) para no chocar con su badge propio */}
                        {!ultimoAbierto.isContinentalIPC && !ultimoAbierto.isAutoridadAD && (
                          <div className="absolute -top-2 left-3 z-20 flex items-center gap-1 bg-blue-600 text-white text-xs font-medium px-2 py-0.5 rounded-full shadow-sm">
                            <History className="h-3 w-3" />
                            Último abierto
                          </div>
                        )}
                        {renderCard(ultimoAbierto)}
                      </div>
                    )}
                    {/* Top 5 más usados (ya excluyen al "último abierto" si coincidía) */}
                    {topDocs.map(doc => (
                      <React.Fragment key={`top-${doc.id}`}>{renderCard(doc, { key: `top-${doc.id}` })}</React.Fragment>
                    ))}
                  </div>
                  <div className="border-b border-gray-200 mt-6"></div>
                </>
              )}
            </div>
          )}

          {/* TAB: MANUALES */}
          <TabsContent value="manuales">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-700">Manuales Técnicos</h2>
              <p className="text-sm text-gray-500">Manual de Mantenimiento, Service Manual, Catálogo de Partes, Manual de Prácticas Estándar, Overhaul Manual</p>
            </div>
            {renderDocumentGrid(
              listaManuales,
              "No hay manuales que coincidan con los filtros",
              mostrarTarjetaContinental ? <ContinentalIPCCard key="continental-ipc" /> : null
            )}
          </TabsContent>

          {/* TAB: PUBLICACIONES DE SERVICIO */}
          <TabsContent value="publicaciones_servicio">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-700">Publicaciones de Servicio</h2>
              <p className="text-sm text-gray-500">Directivas de Aeronavegabilidad, Service Bulletins</p>
            </div>
            {renderDocumentGrid(
              listaPubServicio,
              "No hay publicaciones de servicio que coincidan con los filtros",
              tarjetasAutoridadVisibles.length > 0 ? (
                <React.Fragment key="autoridad-ad-cards">
                  {tarjetasAutoridadVisibles.map(c => (
                    <AutoridadADCard key={c.id} config={c} />
                  ))}
                </React.Fragment>
              ) : null
            )}
          </TabsContent>

          {/* TAB: OTRAS PUBLICACIONES */}
          <TabsContent value="otras">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-700">Otras Publicaciones</h2>
              <p className="text-sm text-gray-500">Service Letters, Service Instructions, Service Advisory, Circular Técnica</p>
            </div>
            {renderDocumentGrid(listaOtras, "No hay otras publicaciones que coincidan con los filtros")}
          </TabsContent>
        </Tabs>

        {/* PDF Viewer Pro - Visor Premium para Suscriptores */}
        <PDFViewerPro
          isOpen={showPdfViewer}
          onClose={() => setShowPdfViewer(false)}
          documentId={selectedDoc?.id}
          documentName={selectedDoc?.nombre}
          numeroReferencia={selectedDoc?.numero_referencia}
          canPrint={getPermisosImpresion().puede_imprimir}
          maxPrintPages={getPermisosImpresion().max_paginas}
          isAdmin={false}
          isSuperAdmin={false}
          userInfo={user}
        />

        {/* Overlay inmediato al abrir desde link de notificación */}
        {notifOpening && (
          <div
            data-testid="notif-open-overlay"
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            style={{
              background:
                'radial-gradient(1200px 600px at 20% 10%, rgba(30,64,175,0.35) 0%, rgba(0,0,0,0.85) 60%), linear-gradient(135deg, #0b1220 0%, #111827 100%)',
            }}
          >
            {/* grano/ruido sutil */}
            <div
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                backgroundImage:
                  'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)',
                backgroundSize: '3px 3px',
              }}
            />
            <div className="relative flex flex-col items-center gap-6 max-w-xl px-6 text-center">
              {/* Spinner con anillo doble */}
              <div className="relative h-24 w-24">
                <div className="absolute inset-0 rounded-full border-4 border-orange-500/20" />
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-orange-500 border-r-orange-400 animate-spin" />
                <div className="absolute inset-3 rounded-full border-2 border-blue-400/30" />
                <div className="absolute inset-3 rounded-full border-2 border-transparent border-b-blue-400 animate-spin" style={{ animationDuration: '2.4s', animationDirection: 'reverse' }} />
                <BookOpen className="absolute inset-0 m-auto h-8 w-8 text-orange-400" />
              </div>

              <div className="space-y-2">
                <p className="text-sm uppercase tracking-[0.3em] text-orange-400/90 font-semibold">
                  Abriendo documento
                </p>
                <p
                  key={notifQuoteIdx}
                  data-testid="notif-open-quote"
                  className="text-white text-lg sm:text-xl leading-relaxed font-light animate-in fade-in duration-700"
                >
                  {AVIATION_QUOTES[notifQuoteIdx]}
                </p>
              </div>

              <p className="text-xs text-gray-400">
                Esto puede tomar unos segundos la primera vez. No cierres esta ventana.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TallerDocumentos;
